import {
  ClientReportingProgram,
  LoadClientReportingProgramsParams,
} from '@types'

interface LoadClientReportingProgramsCoordinator {
  RestService: {
    get: (
      path: string,
      params: LoadClientReportingProgramsParams,
    ) => Promise<ClientReportingProgram[]>
  }
  AccountService: {
    setClientReportingPrograms: (programs: ClientReportingProgram[]) => void
  }
  HandleError: any
}
export const LoadClientReportingPrograms =
  ({
    RestService,
    AccountService,
    HandleError,
  }: LoadClientReportingProgramsCoordinator) =>
  async (params: LoadClientReportingProgramsParams) => {
    const req = {} as LoadClientReportingProgramsParams
    if (params.name) {
      req.name = params.name
    }
    if (params.clientId) {
      req.clientId = params.clientId
    }
    if (params.clientIds) {
      if (Array.isArray(params.clientIds)) {
        req.clientIds = params.clientIds.join(',')
      } else {
        req.clientIds = params.clientIds
      }
    }
    if (params.dinerProfileIds) {
      if (Array.isArray(params.dinerProfileIds)) {
        req.dinerProfileIds = params.dinerProfileIds.join(',')
      } else {
        req.dinerProfileIds = params.dinerProfileIds
      }
    }
    if (params.clientSettingsIds) {
      if (Array.isArray(params.clientSettingsIds)) {
        req.clientSettingsIds = params.clientSettingsIds.join(',')
      } else {
        req.clientSettingsIds = params.clientSettingsIds
      }
    }
    if (params.isDefault !== undefined) {
      req.isDefault = params.isDefault
    }
    if (params.limit) {
      req.limit = params.limit
    }
    if (params.offset) {
      req.offset = params.offset
    }

    try {
      const reportingPrograms = await RestService.get(
        '/client-reporting-programs',
        req,
      )
      AccountService.setClientReportingPrograms(reportingPrograms)

      return reportingPrograms
    } catch (error) {
      HandleError({ error })

      return []
    }
  }

interface SearchClientReportingProgramsCoordinator {
  RestService: {
    get: (
      path: string,
      params: LoadClientReportingProgramsParams,
    ) => Promise<ClientReportingProgram[]>
  }
  HandleError: any
}
export const SearchClientReportingPrograms =
  ({ RestService, HandleError }: SearchClientReportingProgramsCoordinator) =>
  async (params: LoadClientReportingProgramsParams) => {
    const req = {} as LoadClientReportingProgramsParams
    if (params.name) {
      req.name = params.name
    }
    if (params.clientId) {
      req.clientId = params.clientId
    }
    if (params.clientIds) {
      if (Array.isArray(params.clientIds)) {
        req.clientIds = params.clientIds.join(',')
      } else {
        req.clientIds = params.clientIds
      }
    }
    if (params.dinerProfileIds) {
      if (Array.isArray(params.dinerProfileIds)) {
        req.dinerProfileIds = params.dinerProfileIds.join(',')
      } else {
        req.dinerProfileIds = params.dinerProfileIds
      }
    }
    if (params.clientSettingsIds) {
      if (Array.isArray(params.clientSettingsIds)) {
        req.clientSettingsIds = params.clientSettingsIds.join(',')
      } else {
        req.clientSettingsIds = params.clientSettingsIds
      }
    }
    if (params.isDefault) {
      req.isDefault = params.isDefault
    }
    if (params.limit) {
      req.limit = params.limit
    }
    if (params.offset) {
      req.offset = params.offset
    }

    try {
      const reportingPrograms = await RestService.get(
        '/client-reporting-programs/search',
        req,
      )

      return reportingPrograms
    } catch (error) {
      HandleError({ error })

      return []
    }
  }

interface LoadClientReportingProgramCoordinator {
  RestService: {
    get: (path: string) => Promise<ClientReportingProgram>
  }
  HandleError: any
}
type GetClientReportingProgramBy = 'client' | 'dinerProfile' | 'id'
export const LoadClientReportingProgram =
  ({ RestService, HandleError }: LoadClientReportingProgramCoordinator) =>
  async (
    id: string,
    getBy: GetClientReportingProgramBy | undefined = undefined,
  ) => {
    let uri: string
    if (!getBy || getBy === 'id') {
      uri = `/client-reporting-programs/${id}`
    } else if (getBy === 'client') {
      uri = `/client-reporting-programs/client/${id}`
    } else {
      uri = `/client-reporting-programs/diner-profile/${id}`
    }

    try {
      const reportingProgram = await RestService.get(uri)

      return reportingProgram
    } catch (error) {
      HandleError({ error })

      return null
    }
  }

interface SaveClientReportingProgramCoordinator {
  RestService: {
    post: (
      path: string,
      data: ClientReportingProgram,
    ) => Promise<ClientReportingProgram>
    put: (
      path: string,
      data: ClientReportingProgram,
    ) => Promise<ClientReportingProgram>
  }
  AccountService: {
    clearEditClientReportingProgram: () => void
  }
  UIService: {
    EditClientReportingProgram: {
      close: () => void
    }
  }
  HandleError: any
}
export const SaveClientReportingProgram =
  ({
    RestService,
    AccountService,
    UIService,
    HandleError,
  }: SaveClientReportingProgramCoordinator) =>
  async (data: ClientReportingProgram) => {
    try {
      let reportingProgram: ClientReportingProgram
      if (data.id) {
        reportingProgram = await RestService.put(
          `/client-reporting-programs`,
          data,
        )
      } else {
        reportingProgram = await RestService.post(
          `/client-reporting-programs`,
          data,
        )
      }

      AccountService.clearEditClientReportingProgram()
      UIService.EditClientReportingProgram.close()

      return reportingProgram
    } catch (error) {
      HandleError({ error })

      return null
    }
  }

interface EditClientReportingProgramCoodinator {
  AccountService: {
    setEditClientReportingProgram: (
      reportingProgram: ClientReportingProgram,
    ) => void
    clearEditClientReportingProgram: () => void
  }
  UIService: {
    EditClientReportingProgram: {
      show: () => void
      close: () => void
    }
  }
}
export const EditClientReportingProgram =
  ({ AccountService, UIService }: EditClientReportingProgramCoodinator) =>
  (reportingProgram: ClientReportingProgram) => {
    AccountService.setEditClientReportingProgram(reportingProgram)
    UIService.EditClientReportingProgram.show()
  }
export const CloseEditClientReportingProgramModal =
  ({ AccountService, UIService }: EditClientReportingProgramCoodinator) =>
  () => {
    AccountService.clearEditClientReportingProgram()
    UIService.EditClientReportingProgram.close()
  }

interface SyncClientReportingProgramOrdersCoordinator {
  RestService: {
    post: (path: string) => Promise<string>
  }
  UIService: {
    FlashMessage: {
      displaySuccessMessage: (mssg: string) => void
    }
  }
  HandleError: any
}

export const SyncClientReportingProgramOrders =
  ({
    RestService,
    UIService,
    HandleError,
  }: SyncClientReportingProgramOrdersCoordinator) =>
  async (id: string): Promise<boolean> => {
    try {
      const resp = await RestService.post(
        `/client-reporting-programs/${id}/sync_orders`,
      )
      UIService.FlashMessage.displaySuccessMessage(resp)
    } catch (error) {
      HandleError({ error })

      return false
    }

    return true
  }
